import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, RefreshCw, Minus, Plus } from 'lucide-react';
// === TYPES ===
interface LoanRow {
  loanNumber: string;
  clientName: string;
  loanStage: string;
  imported: string;
  submitted: string;
  teamLead: string;
  processor: string;
  activity: string;
  lender: string;
  type: string;
  amount: string;
  st: string;
  lockExp: string;
  cd: string;
  openCond: number;
}
interface PipelineStage {
  name: string;
  loanCount: number;
  volume: string;
  loans: LoanRow[];
}
// === KPI DATA ===
const KPI_CARDS = [{
  label: 'TOTAL VOLUME',
  value: '$105,882,230',
  icon: '$'
}, {
  label: 'TOTAL UNITS',
  value: '426',
  icon: '#'
}, {
  label: 'AVG LOAN SIZE',
  value: '$262,735',
  icon: '+'
}, {
  label: 'NOT LOCKED',
  value: '0',
  icon: '!'
}, {
  label: 'CDS SENT',
  value: '0',
  icon: '='
}];
// === TABLE COLUMNS ===
const TABLE_COLUMNS = ['LOAN #', 'CLIENT NAME', 'LOAN STAGE', 'IMPORTED', 'SUBMITTED', 'TEAM LEAD', 'PROCESSOR', 'ACTIVITY', 'LENDER', 'TYPE', 'AMOUNT', 'ST', 'LOCK EXP', 'CD', 'OPEN COND.'];
// === MOCK DATA ===
const CLOSING_LOANS: LoanRow[] = [{
  loanNumber: '1526102237',
  clientName: 'Tyrone Hall',
  loanStage: 'Closing',
  imported: 'Mar 02, 2026, 11:20 AM',
  submitted: 'Mar 02, 2026, 02:18 PM',
  teamLead: 'Sulvin Youanis',
  processor: 'Gabriela Dalal',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$596,000',
  st: 'CA',
  lockExp: 'May 07, 2026',
  cd: 'Empty',
  openCond: 7
}, {
  loanNumber: '1526028532',
  clientName: 'Kenneth Craft',
  loanStage: 'Closing',
  imported: 'Jan 19, 2026, 05:34 PM',
  submitted: 'Jan 20, 2026, 02:43 PM',
  teamLead: 'Jaden Jappaya',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$171,855',
  st: 'KY',
  lockExp: 'May 07, 2026',
  cd: 'Empty',
  openCond: 0
}, {
  loanNumber: '1526165719',
  clientName: 'Edward Bernal',
  loanStage: 'Closing',
  imported: 'Apr 08, 2026, 06:15 PM',
  submitted: 'Apr 09, 2026, 09:35 AM',
  teamLead: 'Alex Mannhalt',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: 'Empty',
  st: 'FL',
  lockExp: 'May 05, 2026',
  cd: 'Need',
  openCond: 3
}, {
  loanNumber: '1526131401',
  clientName: 'Dionne Wilson',
  loanStage: 'Closing',
  imported: 'Mar 17, 2026, 04:06 PM',
  submitted: 'Mar 19, 2026, 04:30 PM',
  teamLead: 'Sulvin Youanis',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$288,000',
  st: 'AL',
  lockExp: 'May 06, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526130472',
  clientName: 'DAVID PALMER',
  loanStage: 'Closing',
  imported: 'Mar 17, 2026, 11:58 AM',
  submitted: 'Mar 17, 2026, 05:22 PM',
  teamLead: 'Alex Mannhalt',
  processor: 'Jacqueline Martin',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$222,000',
  st: 'KY',
  lockExp: 'Apr 22, 2026',
  cd: 'Empty',
  openCond: 11
}, {
  loanNumber: '1525418473',
  clientName: 'Marc Mertz',
  loanStage: 'Closing',
  imported: 'Dec 10, 2025, 02:37 PM',
  submitted: 'Dec 11, 2025, 09:56 AM',
  teamLead: 'Sulvin Youanis',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$100,000',
  st: 'AR',
  lockExp: 'May 05, 2026',
  cd: 'Empty',
  openCond: 0
}, {
  loanNumber: '1526131543',
  clientName: 'Mark Anderson',
  loanStage: 'Closing',
  imported: 'Mar 17, 2026, 04:43 PM',
  submitted: 'Mar 18, 2026, 03:03 PM',
  teamLead: 'Leonard Roberts',
  processor: 'Mirna Lateef',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$104,000',
  st: 'NC',
  lockExp: 'May 05, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526153444',
  clientName: 'Jacqueline Greene',
  loanStage: 'Closing',
  imported: 'Apr 01, 2026, 10:28 AM',
  submitted: 'Apr 03, 2026, 11:06 AM',
  teamLead: 'Evan Nagara',
  processor: 'Chris Dennis',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: 'Empty',
  st: 'GA',
  lockExp: 'May 05, 2026',
  cd: 'Need',
  openCond: 11
}, {
  loanNumber: '1526058446',
  clientName: 'Donald Payne',
  loanStage: 'Closing',
  imported: 'Feb 05, 2026, 03:08 PM',
  submitted: 'Feb 06, 2026, 11:26 AM',
  teamLead: 'Alex Mannhalt',
  processor: 'Scott Smith',
  activity: 'Apr 14, 2026',
  lender: 'UWM',
  type: 'VA',
  amount: '$234,000',
  st: 'AL',
  lockExp: 'Apr 22, 2026',
  cd: 'Empty',
  openCond: 5
}, {
  loanNumber: '1526105493',
  clientName: 'Bradley Snyder',
  loanStage: 'Closing',
  imported: 'Mar 03, 2026, 02:18 PM',
  submitted: 'Mar 04, 2026, 02:14 PM',
  teamLead: 'Alex Mannhalt',
  processor: 'Ethan Dunn',
  activity: 'Apr 06, 2026',
  lender: 'UWM',
  type: 'CONV',
  amount: '$90,000',
  st: 'IL',
  lockExp: 'Apr 28, 2026',
  cd: 'Empty',
  openCond: 11
}, {
  loanNumber: '1526123032',
  clientName: 'James Barnes',
  loanStage: 'Closing',
  imported: 'Mar 12, 2026, 09:43 AM',
  submitted: 'Mar 13, 2026, 10:09 AM',
  teamLead: 'Alex Mannhalt',
  processor: 'Mirna Lateef',
  activity: 'Mar 16, 2026',
  lender: 'UWM',
  type: 'FHA',
  amount: '$127,000',
  st: 'MT',
  lockExp: 'May 04, 2026',
  cd: 'Empty',
  openCond: 11
}, {
  loanNumber: '1526135271',
  clientName: 'Joseph Hendershot',
  loanStage: 'Closing',
  imported: 'Mar 19, 2026, 02:20 PM',
  submitted: 'Mar 19, 2026, 06:23 PM',
  teamLead: 'Lance Ibrahim',
  processor: 'Chris Dennis',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$340,000',
  st: 'NJ',
  lockExp: 'Apr 23, 2026',
  cd: 'Empty',
  openCond: 8
}, {
  loanNumber: '1526137016',
  clientName: 'Rick Baity',
  loanStage: 'Closing',
  imported: 'Mar 20, 2026, 01:01 PM',
  submitted: 'Mar 20, 2026, 04:56 PM',
  teamLead: 'Yousif Oraha',
  processor: 'Jacqueline Martin',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$279,530',
  st: 'MD',
  lockExp: 'Apr 27, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526139601',
  clientName: 'Yolanda Southard',
  loanStage: 'Closing',
  imported: 'Mar 23, 2026, 02:53 PM',
  submitted: 'Mar 30, 2026, 04:33 PM',
  teamLead: 'Max Fazio',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$212,500',
  st: 'FL',
  lockExp: 'Apr 29, 2026',
  cd: 'Empty',
  openCond: 7
}, {
  loanNumber: '1526139884',
  clientName: 'Jerry Jackson',
  loanStage: 'Closing',
  imported: 'Mar 23, 2026, 04:05 PM',
  submitted: 'Mar 24, 2026, 05:14 PM',
  teamLead: 'Lance Ibrahim',
  processor: 'Ethan Dunn',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$145,000',
  st: 'MD',
  lockExp: 'Apr 23, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526140185',
  clientName: 'Larry Lewis',
  loanStage: 'Closing',
  imported: 'Mar 23, 2026, 05:33 PM',
  submitted: 'Mar 24, 2026, 02:29 PM',
  teamLead: 'Sal Naemi',
  processor: 'Ethan Dunn',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$165,000',
  st: 'OH',
  lockExp: 'Apr 30, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526143032',
  clientName: 'CHARLES THAYER',
  loanStage: 'Closing',
  imported: 'Mar 25, 2026, 11:41 AM',
  submitted: 'Mar 25, 2026, 03:38 PM',
  teamLead: 'Kristen Denha',
  processor: 'Gabriela Dalal',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$280,000',
  st: 'NE',
  lockExp: 'Apr 30, 2026',
  cd: 'Empty',
  openCond: 7
}, {
  loanNumber: '1526147528',
  clientName: 'Gerard Dugas',
  loanStage: 'Closing',
  imported: 'Mar 27, 2026, 02:56 PM',
  submitted: 'Mar 27, 2026, 03:45 PM',
  teamLead: 'Leonard Roberts',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$160,000',
  st: 'NH',
  lockExp: 'Apr 28, 2026',
  cd: 'Need',
  openCond: 6
}];
const CTC_LOANS: LoanRow[] = [{
  loanNumber: '1526135752',
  clientName: 'Sharon Dotson',
  loanStage: 'CTC',
  imported: 'Mar 19, 2026, 04:32 PM',
  submitted: 'Mar 20, 2026, 01:03 PM',
  teamLead: 'Leonard Roberts',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$220,000',
  st: 'OH',
  lockExp: 'May 06, 2026',
  cd: 'Empty',
  openCond: 12
}, {
  loanNumber: '57026034801',
  clientName: 'Herbert Williams',
  loanStage: 'CTC',
  imported: 'Mar 10, 2026, 12:00 PM',
  submitted: 'Mar 11, 2026, 10:17 AM',
  teamLead: 'Sulvin Youanis',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'Village Capital',
  type: 'FHA',
  amount: '$458,000',
  st: 'IL',
  lockExp: 'Empty',
  cd: 'Empty',
  openCond: 10
}, {
  loanNumber: '1526087977',
  clientName: 'Linda Graham',
  loanStage: 'CTC',
  imported: 'Feb 23, 2026, 11:24 AM',
  submitted: 'Feb 25, 2026, 01:23 PM',
  teamLead: 'Alex Mannhalt',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: 'Empty',
  st: 'NJ',
  lockExp: 'May 05, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526145596',
  clientName: 'Ralphetta Davis',
  loanStage: 'CTC',
  imported: 'Mar 26, 2026, 02:13 PM',
  submitted: 'Mar 26, 2026, 05:08 PM',
  teamLead: 'Max Fazio',
  processor: 'Scott Smith',
  activity: 'Apr 14, 2026',
  lender: 'UWM',
  type: 'FHA',
  amount: '$220,000',
  st: 'SC',
  lockExp: 'Apr 29, 2026',
  cd: 'Need',
  openCond: 11
}, {
  loanNumber: '1526113140',
  clientName: 'John Dinges',
  loanStage: 'CTC',
  imported: 'Mar 06, 2026, 02:17 PM',
  submitted: 'Mar 06, 2026, 05:12 PM',
  teamLead: 'Kristen Denha',
  processor: 'Chris Dennis',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$274,000',
  st: 'SC',
  lockExp: 'May 05, 2026',
  cd: 'Empty',
  openCond: 11
}, {
  loanNumber: '1526160536',
  clientName: 'ROSALINA Moyer',
  loanStage: 'CTC',
  imported: 'Apr 06, 2026, 03:02 PM',
  submitted: 'Apr 13, 2026, 12:48 PM',
  teamLead: 'Kristen Denha',
  processor: 'Mirna Lateef',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$149,000',
  st: 'DE',
  lockExp: 'May 06, 2026',
  cd: 'Need',
  openCond: 7
}, {
  loanNumber: '1526118414',
  clientName: 'Leroy Harris',
  loanStage: 'CTC',
  imported: 'Mar 10, 2026, 12:02 PM',
  submitted: 'Mar 10, 2026, 02:57 PM',
  teamLead: 'Lance Ibrahim',
  processor: 'Jacqueline Martin',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$240,000',
  st: 'WY',
  lockExp: 'May 05, 2026',
  cd: 'Empty',
  openCond: 5
}, {
  loanNumber: '1526089412',
  clientName: 'Chad Givens',
  loanStage: 'CTC',
  imported: 'Feb 23, 2026, 05:09 PM',
  submitted: 'Mar 23, 2026, 09:46 AM',
  teamLead: 'James England',
  processor: 'Mirna Lateef',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$50,000',
  st: 'AL',
  lockExp: 'Apr 23, 2026',
  cd: 'Empty',
  openCond: 0
}, {
  loanNumber: '1526108618',
  clientName: 'James Gerton',
  loanStage: 'CTC',
  imported: 'Mar 04, 2026, 04:33 PM',
  submitted: 'Mar 05, 2026, 12:31 PM',
  teamLead: 'Sulvin Youanis',
  processor: 'Jacqueline Martin',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$226,000',
  st: 'KY',
  lockExp: 'Apr 07, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526133373',
  clientName: 'CHRISTOPHER POOLE',
  loanStage: 'CTC',
  imported: 'Mar 18, 2026, 03:07 PM',
  submitted: 'Mar 18, 2026, 06:10 PM',
  teamLead: 'Sulvin Youanis',
  processor: 'Chris Dennis',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$316,000',
  st: 'FL',
  lockExp: 'Apr 23, 2026',
  cd: 'Empty',
  openCond: 5
}, {
  loanNumber: '57026039079',
  clientName: 'CURTIS SIMS',
  loanStage: 'CTC',
  imported: 'Mar 17, 2026, 11:27 AM',
  submitted: 'Mar 25, 2026, 05:21 PM',
  teamLead: 'Lance Ibrahim',
  processor: 'Mirna Lateef',
  activity: 'Empty',
  lender: 'Village Capital',
  type: 'VA',
  amount: '$340,000',
  st: 'FL',
  lockExp: 'Empty',
  cd: 'Empty',
  openCond: 3
}];
const ALL_IN_LOANS: LoanRow[] = [{
  loanNumber: '1526148901',
  clientName: 'Robert Chen',
  loanStage: 'All In',
  imported: 'Mar 28, 2026, 09:15 AM',
  submitted: 'Mar 28, 2026, 02:30 PM',
  teamLead: 'Alex Mannhalt',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$345,000',
  st: 'TX',
  lockExp: 'May 10, 2026',
  cd: 'Empty',
  openCond: 4
}, {
  loanNumber: '1526150234',
  clientName: 'Maria Santos',
  loanStage: 'All In',
  imported: 'Mar 29, 2026, 11:45 AM',
  submitted: 'Mar 30, 2026, 09:20 AM',
  teamLead: 'Sulvin Youanis',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$278,500',
  st: 'FL',
  lockExp: 'May 08, 2026',
  cd: 'Empty',
  openCond: 8
}, {
  loanNumber: '1526151567',
  clientName: 'William Turner',
  loanStage: 'All In',
  imported: 'Mar 30, 2026, 02:10 PM',
  submitted: 'Mar 31, 2026, 10:45 AM',
  teamLead: 'Leonard Roberts',
  processor: 'Ethan Dunn',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$412,000',
  st: 'VA',
  lockExp: 'May 12, 2026',
  cd: 'Need',
  openCond: 3
}, {
  loanNumber: '1526152890',
  clientName: 'Jennifer Adams',
  loanStage: 'All In',
  imported: 'Mar 31, 2026, 08:30 AM',
  submitted: 'Apr 01, 2026, 11:15 AM',
  teamLead: 'Max Fazio',
  processor: 'Chris Dennis',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$195,000',
  st: 'OH',
  lockExp: 'May 06, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526154123',
  clientName: 'Thomas Wright',
  loanStage: 'All In',
  imported: 'Apr 01, 2026, 03:20 PM',
  submitted: 'Apr 02, 2026, 09:00 AM',
  teamLead: 'Kristen Denha',
  processor: 'Gabriela Dalal',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$310,000',
  st: 'GA',
  lockExp: 'May 09, 2026',
  cd: 'Empty',
  openCond: 5
}, {
  loanNumber: '1526155456',
  clientName: 'Patricia Moore',
  loanStage: 'All In',
  imported: 'Apr 02, 2026, 10:00 AM',
  submitted: 'Apr 02, 2026, 04:30 PM',
  teamLead: 'Lance Ibrahim',
  processor: 'Mirna Lateef',
  activity: 'Apr 10, 2026',
  lender: 'UWM',
  type: 'VA',
  amount: '$267,075',
  st: 'NC',
  lockExp: 'May 11, 2026',
  cd: 'Empty',
  openCond: 9
}, {
  loanNumber: '1526156789',
  clientName: 'David Kim',
  loanStage: 'All In',
  imported: 'Apr 03, 2026, 01:45 PM',
  submitted: 'Apr 04, 2026, 10:20 AM',
  teamLead: 'Evan Nagara',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$520,000',
  st: 'CA',
  lockExp: 'May 15, 2026',
  cd: 'Empty',
  openCond: 2
}, {
  loanNumber: '1526158012',
  clientName: 'Lisa Johnson',
  loanStage: 'All In',
  imported: 'Apr 04, 2026, 09:30 AM',
  submitted: 'Apr 04, 2026, 03:15 PM',
  teamLead: 'Yousif Oraha',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$189,000',
  st: 'MI',
  lockExp: 'May 07, 2026',
  cd: 'Need',
  openCond: 7
}, {
  loanNumber: '1526159345',
  clientName: 'Kevin Brown',
  loanStage: 'All In',
  imported: 'Apr 05, 2026, 11:15 AM',
  submitted: 'Apr 06, 2026, 08:45 AM',
  teamLead: 'Sal Naemi',
  processor: 'Ethan Dunn',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$332,000',
  st: 'AZ',
  lockExp: 'May 13, 2026',
  cd: 'Empty',
  openCond: 4
}];
const AWC_LOANS: LoanRow[] = [{
  loanNumber: '1526120001',
  clientName: 'Angela Foster',
  loanStage: 'AWC',
  imported: 'Mar 11, 2026, 09:00 AM',
  submitted: 'Mar 12, 2026, 10:30 AM',
  teamLead: 'Alex Mannhalt',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$245,000',
  st: 'TX',
  lockExp: 'May 01, 2026',
  cd: 'Empty',
  openCond: 3
}, {
  loanNumber: '1526120102',
  clientName: 'Brian Mitchell',
  loanStage: 'AWC',
  imported: 'Mar 11, 2026, 10:15 AM',
  submitted: 'Mar 12, 2026, 02:00 PM',
  teamLead: 'Sulvin Youanis',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$312,000',
  st: 'FL',
  lockExp: 'Apr 28, 2026',
  cd: 'Empty',
  openCond: 5
}, {
  loanNumber: '1526120203',
  clientName: 'Carol Henderson',
  loanStage: 'AWC',
  imported: 'Mar 11, 2026, 11:30 AM',
  submitted: 'Mar 13, 2026, 09:45 AM',
  teamLead: 'Leonard Roberts',
  processor: 'Ethan Dunn',
  activity: 'Mar 20, 2026',
  lender: 'UWM',
  type: 'CONV',
  amount: '$189,500',
  st: 'OH',
  lockExp: 'May 05, 2026',
  cd: 'Need',
  openCond: 8
}, {
  loanNumber: '1526120304',
  clientName: 'Derek Washington',
  loanStage: 'AWC',
  imported: 'Mar 12, 2026, 08:45 AM',
  submitted: 'Mar 12, 2026, 04:20 PM',
  teamLead: 'Max Fazio',
  processor: 'Chris Dennis',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$275,000',
  st: 'GA',
  lockExp: 'Apr 30, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526120405',
  clientName: 'Emily Rodriguez',
  loanStage: 'AWC',
  imported: 'Mar 12, 2026, 10:00 AM',
  submitted: 'Mar 13, 2026, 11:15 AM',
  teamLead: 'Kristen Denha',
  processor: 'Gabriela Dalal',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$198,000',
  st: 'NC',
  lockExp: 'May 02, 2026',
  cd: 'Empty',
  openCond: 4
}, {
  loanNumber: '1526120506',
  clientName: 'Frank Peterson',
  loanStage: 'AWC',
  imported: 'Mar 12, 2026, 01:30 PM',
  submitted: 'Mar 14, 2026, 09:00 AM',
  teamLead: 'Lance Ibrahim',
  processor: 'Mirna Lateef',
  activity: 'Empty',
  lender: 'Village Capital',
  type: 'CONV',
  amount: '$425,000',
  st: 'CA',
  lockExp: 'May 08, 2026',
  cd: 'Empty',
  openCond: 2
}, {
  loanNumber: '1526120607',
  clientName: 'Grace Thompson',
  loanStage: 'AWC',
  imported: 'Mar 13, 2026, 09:15 AM',
  submitted: 'Mar 13, 2026, 03:45 PM',
  teamLead: 'Evan Nagara',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$167,000',
  st: 'AL',
  lockExp: 'Apr 25, 2026',
  cd: 'Empty',
  openCond: 9
}, {
  loanNumber: '1526120708',
  clientName: 'Henry Clark',
  loanStage: 'AWC',
  imported: 'Mar 13, 2026, 11:00 AM',
  submitted: 'Mar 14, 2026, 10:30 AM',
  teamLead: 'Yousif Oraha',
  processor: 'Jacqueline Martin',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$356,000',
  st: 'VA',
  lockExp: 'May 03, 2026',
  cd: 'Empty',
  openCond: 7
}, {
  loanNumber: '1526120809',
  clientName: 'Irene Davis',
  loanStage: 'AWC',
  imported: 'Mar 14, 2026, 08:30 AM',
  submitted: 'Mar 14, 2026, 02:15 PM',
  teamLead: 'Sal Naemi',
  processor: 'Michael Kane',
  activity: 'Apr 01, 2026',
  lender: 'UWM',
  type: 'CONV',
  amount: '$290,000',
  st: 'MI',
  lockExp: 'May 06, 2026',
  cd: 'Need',
  openCond: 5
}, {
  loanNumber: '1526120910',
  clientName: 'Jack Robinson',
  loanStage: 'AWC',
  imported: 'Mar 14, 2026, 10:45 AM',
  submitted: 'Mar 15, 2026, 09:30 AM',
  teamLead: 'Alex Mannhalt',
  processor: 'Ethan Dunn',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$215,000',
  st: 'PA',
  lockExp: 'Apr 29, 2026',
  cd: 'Empty',
  openCond: 3
}, {
  loanNumber: '1526121011',
  clientName: 'Karen Lewis',
  loanStage: 'AWC',
  imported: 'Mar 15, 2026, 09:00 AM',
  submitted: 'Mar 16, 2026, 11:00 AM',
  teamLead: 'Sulvin Youanis',
  processor: 'Chris Dennis',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$178,500',
  st: 'TN',
  lockExp: 'May 04, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526121112',
  clientName: 'Larry Martin',
  loanStage: 'AWC',
  imported: 'Mar 15, 2026, 11:30 AM',
  submitted: 'Mar 16, 2026, 02:45 PM',
  teamLead: 'Leonard Roberts',
  processor: 'Gabriela Dalal',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$340,000',
  st: 'AZ',
  lockExp: 'May 07, 2026',
  cd: 'Empty',
  openCond: 4
}];
const UW_LOANS: LoanRow[] = [{
  loanNumber: '1526160001',
  clientName: 'Nathan Price',
  loanStage: 'Underwriting',
  imported: 'Apr 06, 2026, 09:00 AM',
  submitted: 'Apr 07, 2026, 10:30 AM',
  teamLead: 'Alex Mannhalt',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$285,000',
  st: 'TX',
  lockExp: 'May 15, 2026',
  cd: 'Empty',
  openCond: 5
}, {
  loanNumber: '1526160102',
  clientName: 'Olivia Barnes',
  loanStage: 'Underwriting',
  imported: 'Apr 06, 2026, 11:15 AM',
  submitted: 'Apr 07, 2026, 02:00 PM',
  teamLead: 'Sulvin Youanis',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$198,000',
  st: 'FL',
  lockExp: 'May 12, 2026',
  cd: 'Empty',
  openCond: 8
}, {
  loanNumber: '1526160203',
  clientName: 'Peter Collins',
  loanStage: 'Underwriting',
  imported: 'Apr 07, 2026, 08:45 AM',
  submitted: 'Apr 08, 2026, 09:30 AM',
  teamLead: 'Leonard Roberts',
  processor: 'Ethan Dunn',
  activity: 'Apr 12, 2026',
  lender: 'UWM',
  type: 'VA',
  amount: '$367,000',
  st: 'VA',
  lockExp: 'May 18, 2026',
  cd: 'Need',
  openCond: 3
}, {
  loanNumber: '1526160304',
  clientName: 'Quinn Stewart',
  loanStage: 'Underwriting',
  imported: 'Apr 07, 2026, 10:30 AM',
  submitted: 'Apr 08, 2026, 11:45 AM',
  teamLead: 'Max Fazio',
  processor: 'Chris Dennis',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$225,000',
  st: 'OH',
  lockExp: 'May 14, 2026',
  cd: 'Empty',
  openCond: 6
}, {
  loanNumber: '1526160405',
  clientName: 'Rachel Cooper',
  loanStage: 'Underwriting',
  imported: 'Apr 08, 2026, 09:15 AM',
  submitted: 'Apr 09, 2026, 10:00 AM',
  teamLead: 'Kristen Denha',
  processor: 'Gabriela Dalal',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$312,000',
  st: 'GA',
  lockExp: 'May 16, 2026',
  cd: 'Empty',
  openCond: 4
}, {
  loanNumber: '1526160506',
  clientName: 'Samuel Reed',
  loanStage: 'Underwriting',
  imported: 'Apr 08, 2026, 02:00 PM',
  submitted: 'Apr 09, 2026, 03:30 PM',
  teamLead: 'Lance Ibrahim',
  processor: 'Mirna Lateef',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$445,000',
  st: 'CA',
  lockExp: 'May 20, 2026',
  cd: 'Empty',
  openCond: 2
}, {
  loanNumber: '1526160607',
  clientName: 'Tina Morgan',
  loanStage: 'Underwriting',
  imported: 'Apr 09, 2026, 08:30 AM',
  submitted: 'Apr 10, 2026, 09:15 AM',
  teamLead: 'Evan Nagara',
  processor: 'Scott Smith',
  activity: 'Empty',
  lender: 'Village Capital',
  type: 'FHA',
  amount: '$176,000',
  st: 'AL',
  lockExp: 'May 13, 2026',
  cd: 'Empty',
  openCond: 7
}, {
  loanNumber: '1526160708',
  clientName: 'Ulysses Ward',
  loanStage: 'Underwriting',
  imported: 'Apr 09, 2026, 11:00 AM',
  submitted: 'Apr 10, 2026, 01:45 PM',
  teamLead: 'Yousif Oraha',
  processor: 'Jacqueline Martin',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$298,000',
  st: 'MI',
  lockExp: 'May 17, 2026',
  cd: 'Need',
  openCond: 9
}, {
  loanNumber: '1526160809',
  clientName: 'Vanessa Hill',
  loanStage: 'Underwriting',
  imported: 'Apr 10, 2026, 09:45 AM',
  submitted: 'Apr 11, 2026, 10:30 AM',
  teamLead: 'Sal Naemi',
  processor: 'Michael Kane',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$189,500',
  st: 'NC',
  lockExp: 'May 11, 2026',
  cd: 'Empty',
  openCond: 5
}, {
  loanNumber: '1526160910',
  clientName: 'Walter Young',
  loanStage: 'Underwriting',
  imported: 'Apr 10, 2026, 01:30 PM',
  submitted: 'Apr 11, 2026, 03:00 PM',
  teamLead: 'Alex Mannhalt',
  processor: 'Ethan Dunn',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$256,145',
  st: 'PA',
  lockExp: 'May 19, 2026',
  cd: 'Empty',
  openCond: 3
}];
const STARTED_LOANS: LoanRow[] = [{
  loanNumber: 'Empty',
  clientName: 'MANUEL ORTIZ',
  loanStage: 'Pre-Set Up',
  imported: 'Empty',
  submitted: 'Empty',
  teamLead: 'Julian Yacoub',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$515,000',
  st: 'RI',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 0
}, {
  loanNumber: 'Empty',
  clientName: 'Sean Nunnemaker',
  loanStage: 'Pre-Set Up',
  imported: 'Empty',
  submitted: 'Empty',
  teamLead: 'Leonard Roberts',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$288,000',
  st: 'Empty',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 0
}, {
  loanNumber: 'Empty',
  clientName: 'Laurie Brenner',
  loanStage: 'Set Up',
  imported: 'Apr 16, 2026, 12:00 PM',
  submitted: 'Empty',
  teamLead: 'Cardell Snorton',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'Conventional',
  amount: '$600,000',
  st: 'DC',
  lockExp: 'Empty',
  cd: 'Empty',
  openCond: 10
}, {
  loanNumber: 'Empty',
  clientName: 'Emmanuel Innocent',
  loanStage: 'Pre-Set Up',
  imported: 'Empty',
  submitted: 'Empty',
  teamLead: 'Yousif Oraha',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: 'Empty',
  st: 'NJ',
  lockExp: 'Empty',
  cd: 'Empty',
  openCond: 7
}, {
  loanNumber: '1226264119',
  clientName: 'Ricky Prosen',
  loanStage: 'Docs Out',
  imported: 'Apr 15, 2026, 05:50 PM',
  submitted: 'Empty',
  teamLead: 'James England',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$100,000',
  st: 'PA',
  lockExp: 'Empty',
  cd: 'Empty',
  openCond: 10
}, {
  loanNumber: '1526177067',
  clientName: 'Daniel Sichting',
  loanStage: 'DRI',
  imported: 'Apr 15, 2026, 02:30 PM',
  submitted: 'Empty',
  teamLead: 'Alex Mannhalt',
  processor: 'Empty',
  activity: 'Apr 15, 2026',
  lender: 'UWM',
  type: 'CONV',
  amount: '$125,000',
  st: 'IN',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 11
}, {
  loanNumber: '1526177753',
  clientName: 'GEORGE BAKER',
  loanStage: 'Docs Out',
  imported: 'Apr 15, 2026, 05:22 PM',
  submitted: 'Empty',
  teamLead: 'Cardell Snorton',
  processor: 'Empty',
  activity: 'Apr 15, 2026',
  lender: 'UWM',
  type: 'FHA',
  amount: '$225,000',
  st: 'TN',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 11
}, {
  loanNumber: '1526173697',
  clientName: 'Jaye Gholson',
  loanStage: 'Docs Received',
  imported: 'Apr 14, 2026, 10:34 AM',
  submitted: 'Empty',
  teamLead: 'Kristen Denha',
  processor: 'Mirna Lateef',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$163,000',
  st: 'VA',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 7
}, {
  loanNumber: '1526177519',
  clientName: 'Courtney Woodhouse',
  loanStage: 'Docs Out',
  imported: 'Apr 15, 2026, 04:26 PM',
  submitted: 'Empty',
  teamLead: 'Evan Nagara',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: 'Empty',
  st: 'PA',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 10
}, {
  loanNumber: 'Empty',
  clientName: 'Hector Chamorro',
  loanStage: 'Docs Received',
  imported: 'Mar 20, 2026, 12:00 PM',
  submitted: 'Empty',
  teamLead: 'Leonard Roberts',
  processor: 'Ethan Dunn',
  activity: 'Empty',
  lender: 'Village Capital',
  type: 'IRL',
  amount: '$445,000',
  st: 'FL',
  lockExp: 'Empty',
  cd: 'Empty',
  openCond: 2
}, {
  loanNumber: 'Empty',
  clientName: 'Frank Zalewski',
  loanStage: 'Docs Out',
  imported: 'Apr 09, 2026, 12:00 PM',
  submitted: 'Empty',
  teamLead: 'Alex Mannhalt',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'Village Capital',
  type: 'FHA',
  amount: '$125,000',
  st: 'NJ',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 11
}, {
  loanNumber: '1526177896',
  clientName: 'Alec Jutras',
  loanStage: 'Docs Out',
  imported: 'Apr 15, 2026, 06:02 PM',
  submitted: 'Empty',
  teamLead: 'Alex Mannhalt',
  processor: 'Empty',
  activity: 'Apr 15, 2026',
  lender: 'UWM',
  type: 'FHA',
  amount: '$125,000',
  st: 'NH',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 6
}, {
  loanNumber: '1526175432',
  clientName: 'Marcus Webb',
  loanStage: 'Docs Out',
  imported: 'Apr 15, 2026, 01:15 PM',
  submitted: 'Empty',
  teamLead: 'Sulvin Youanis',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'VA',
  amount: '$310,000',
  st: 'MD',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 8
}, {
  loanNumber: 'Empty',
  clientName: 'Teresa Gomez',
  loanStage: 'Pre-Set Up',
  imported: 'Empty',
  submitted: 'Empty',
  teamLead: 'Max Fazio',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'FHA',
  amount: '$195,000',
  st: 'SC',
  lockExp: 'Empty',
  cd: 'Need',
  openCond: 3
}, {
  loanNumber: '1526176001',
  clientName: 'Anthony Rivera',
  loanStage: 'Set Up',
  imported: 'Apr 15, 2026, 02:00 PM',
  submitted: 'Empty',
  teamLead: 'Lance Ibrahim',
  processor: 'Empty',
  activity: 'Empty',
  lender: 'UWM',
  type: 'CONV',
  amount: '$478,000',
  st: 'NJ',
  lockExp: 'Empty',
  cd: 'Empty',
  openCond: 5
}];
const PIPELINE_STAGES: PipelineStage[] = [{
  name: 'CLOSING',
  loanCount: 34,
  volume: '$7,513,352',
  loans: CLOSING_LOANS
}, {
  name: 'CTC',
  loanCount: 11,
  volume: '$3,009,503',
  loans: CTC_LOANS
}, {
  name: 'ALL IN',
  loanCount: 9,
  volume: '$2,848,575',
  loans: ALL_IN_LOANS
}, {
  name: 'AWC',
  loanCount: 201,
  volume: '$51,132,299',
  loans: AWC_LOANS
}, {
  name: 'SUSPENDED',
  loanCount: 0,
  volume: '$0',
  loans: []
}, {
  name: 'UNDERWRITING',
  loanCount: 30,
  volume: '$7,447,645',
  loans: UW_LOANS
}, {
  name: 'STARTED',
  loanCount: 141,
  volume: '$33,930,856',
  loans: STARTED_LOANS
}];
// === COMPONENTS ===
function KpiCard({
  label,
  value,
  icon,
  index
}: {
  label: string;
  value: string;
  icon: string;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 15
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.08,
    duration: 0.4,
    ease: 'easeOut'
  }} whileHover={{
    y: -3,
    boxShadow: '0 8px 25px -5px rgba(0,0,0,0.3), 0 0 12px rgba(56,189,248,0.1)'
  }} className="flex-1 min-w-[160px] glass-card rounded-xl p-4 flex items-center gap-3 cursor-default shimmer-on-hover" data-id="element-934">
      <div className="w-8 h-8 rounded-full bg-accent-500/20 border border-accent-400/30 flex items-center justify-center shrink-0 icon-glow-pulse" data-id="element-935">
        <span className="text-accent-400 text-sm font-bold" data-id="element-936">{icon}</span>
      </div>
      <div data-id="element-937">
        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider" data-id="element-938">
          {label}
        </p>
        <p className="text-lg font-bold value-glow-cyan" data-id="element-939">{value}</p>
      </div>
    </motion.div>;
}
function StageHeader({
  stage,
  isExpanded,
  onToggle
}: {
  stage: PipelineStage;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return <div data-id="element-940">
      <motion.button onClick={onToggle} whileHover={{
      scale: 1.005
    }} whileTap={{
      scale: 0.998
    }} className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#1a3560] via-[#1e3f6e] to-[#1a3560] rounded-lg hover:from-[#1e3f6e] hover:via-[#244a7a] hover:to-[#1e3f6e] transition-all cursor-pointer shimmer-on-hover hover:shadow-[0_0_25px_rgba(30,58,95,0.5)]" data-id="element-941">
        <div className="flex items-center gap-3" data-id="element-942">
          <motion.div animate={{
          rotate: isExpanded ? 90 : 0
        }} transition={{
          duration: 0.2
        }} data-id="element-943">
            <ChevronRight size={18} className="text-white" data-id="element-944" />
          </motion.div>
          <span className="text-white font-bold text-sm tracking-wide" data-id="element-945">
            {stage.name}
          </span>
          <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-semibold" data-id="element-946">
            {stage.loanCount} loans
          </span>
        </div>
        <div className="flex items-center gap-2" data-id="element-947">
          <span className="text-white/60 text-xs font-semibold uppercase tracking-wider" data-id="element-948">
            Volume
          </span>
          <span className="text-lg font-bold value-glow-emerald" data-id="element-949">
            {stage.volume}
          </span>
        </div>
      </motion.button>
    </div>;
}
function LoanTable({
  loans
}: {
  loans: LoanRow[];
}) {
  if (loans.length === 0) {
    return <div className="px-4 py-8 text-center text-slate-500 text-sm glass-card rounded-b-lg border-t-0" data-id="element-950">
        No loans in this stage
      </div>;
  }
  return <div className="overflow-x-auto glass-card rounded-b-lg border-t-0" data-id="element-951">
      <table className="w-full text-xs" data-id="element-952">
        <thead data-id="element-953">
          <tr className="border-b border-white/10" data-id="element-954">
            {TABLE_COLUMNS.map(col => <th key={col} className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap bg-navy-900/40" data-id="element-955">
                {col}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-956">
          {loans.map((loan, idx) => <tr key={idx} className={`border-b border-white/5 hover:bg-white/[0.03] transition-colors row-stagger ${idx % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]'}`} style={{
          animationDelay: `${idx * 0.03}s`
        }} data-id="element-957">
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap" data-id="element-958">
                {loan.loanNumber === 'Empty' ? <span className="text-slate-600" data-id="element-959">Empty</span> : loan.loanNumber}
              </td>
              <td className="px-3 py-2 text-white font-semibold whitespace-nowrap" data-id="element-960">
                {loan.clientName}
              </td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap" data-id="element-961">
                {loan.loanStage}
              </td>
              <td className="px-3 py-2 text-slate-500 whitespace-nowrap" data-id="element-962">
                {loan.imported === 'Empty' ? <span className="text-slate-600" data-id="element-963">Empty</span> : loan.imported}
              </td>
              <td className="px-3 py-2 text-slate-500 whitespace-nowrap" data-id="element-964">
                {loan.submitted === 'Empty' ? <span className="text-slate-600" data-id="element-965">Empty</span> : loan.submitted}
              </td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap" data-id="element-966">
                {loan.teamLead}
              </td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap" data-id="element-967">
                {loan.processor || <span className="text-slate-600" data-id="element-968">Empty</span>}
              </td>
              <td className="px-3 py-2 text-slate-500 whitespace-nowrap" data-id="element-969">
                {loan.activity === 'Empty' ? <span className="text-slate-600" data-id="element-970">Empty</span> : loan.activity}
              </td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap" data-id="element-971">
                {loan.lender === 'Village Capital' ? <span className="text-red-400 font-medium" data-id="element-972">
                    {loan.lender}
                  </span> : loan.lender}
              </td>
              <td className="px-3 py-2 text-slate-400 whitespace-nowrap" data-id="element-973">
                {loan.type === 'CONV' ? <span className="text-accent-400 font-medium" data-id="element-974">CONV</span> : loan.type}
              </td>
              <td className="px-3 py-2 text-emerald-400 font-medium whitespace-nowrap" data-id="element-975">
                {loan.amount === 'Empty' ? <span className="text-slate-600" data-id="element-976">Empty</span> : loan.amount}
              </td>
              <td className="px-3 py-2 whitespace-nowrap" data-id="element-977">
                {loan.st === 'IL' || loan.st === 'FL' ? <span className="text-amber-400 font-bold" data-id="element-978">{loan.st}</span> : <span className="text-slate-400" data-id="element-979">
                    {loan.st === 'Empty' ? <span className="text-slate-600" data-id="element-980">Empty</span> : loan.st}
                  </span>}
              </td>
              <td className="px-3 py-2 text-slate-500 whitespace-nowrap" data-id="element-981">
                {loan.lockExp === 'Empty' ? <span className="text-slate-600" data-id="element-982">Empty</span> : loan.lockExp}
              </td>
              <td className="px-3 py-2 whitespace-nowrap" data-id="element-983">
                {loan.cd === 'Need' ? <span className="text-red-400 font-medium" data-id="element-984">Need</span> : loan.cd === 'Empty' ? <span className="text-slate-600" data-id="element-985">Empty</span> : loan.cd}
              </td>
              <td className="px-3 py-2 text-slate-400 text-right whitespace-nowrap" data-id="element-986">
                {loan.openCond}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
// === MAIN COMPONENT ===
export function PipelineView() {
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [zoom, setZoom] = useState(100);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const toggleStage = (stageName: string) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(stageName)) {
        next.delete(stageName);
      } else {
        next.add(stageName);
      }
      return next;
    });
  };
  return <div className="max-w-[1600px] mx-auto p-6" style={{
    transform: `scale(${zoom / 100})`,
    transformOrigin: 'top left',
    width: `${10000 / zoom}%`
  }} data-id="element-987">
      {/* Header */}
      <div className="flex items-start justify-between mb-6" data-id="element-988">
        <div data-id="element-989">
          <h1 className="text-2xl font-bold text-white neon-text" data-id="element-990">
            Loan Pipeline
          </h1>
          <p className="text-sm text-slate-400 mt-0.5" data-id="element-991">
            Metadata-driven active pipeline view
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-992">
          <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider mr-1" data-id="element-993">
            Zoom
          </span>
          <button onClick={() => setZoom(z => Math.max(50, z - 10))} className="p-1.5 rounded-lg bg-navy-800/70 border border-white/10 hover:bg-navy-700 transition-colors" data-id="element-994">
            <Minus size={14} className="text-white" data-id="element-995" />
          </button>
          <span className="text-xs font-bold text-white min-w-[36px] text-center" data-id="element-996">
            {zoom}%
          </span>
          <button onClick={() => setZoom(z => Math.min(150, z + 10))} className="p-1.5 rounded-lg bg-navy-800/70 border border-white/10 hover:bg-navy-700 transition-colors" data-id="element-997">
            <Plus size={14} className="text-white" data-id="element-998" />
          </button>
          <button onClick={() => {
          setIsRefreshing(true);
          setTimeout(() => setIsRefreshing(false), 600);
        }} className="ml-2 px-3 py-1.5 rounded-lg bg-navy-800/70 border border-white/10 hover:bg-navy-700 transition-colors text-xs font-semibold text-white flex items-center gap-1.5" data-id="element-999">
            <RefreshCw size={12} className={isRefreshing ? 'spin-once' : ''} data-id="element-1000" />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="flex gap-3 mb-6 overflow-x-auto" data-id="element-1001">
        {KPI_CARDS.map((kpi, i) => <KpiCard key={kpi.label} {...kpi} index={i} data-id="element-1002" />)}
      </div>

      {/* Pipeline Stages */}
      <div className="space-y-3" data-id="element-1003">
        {PIPELINE_STAGES.map(stage => {
        const isExpanded = expandedStages.has(stage.name);
        return <div key={stage.name} data-id="element-1004">
              <StageHeader stage={stage} isExpanded={isExpanded} onToggle={() => toggleStage(stage.name)} data-id="element-1005" />
              <AnimatePresence data-id="element-1006">
                {isExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-1007">
                    <LoanTable loans={stage.loans} data-id="element-1008" />
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>
    </div>;
}